import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { getRequest, postRequest } from '../../requestUtils';
import './ProposalMessages.scss';
var roleLabel = function roleLabel(role) {
  switch (role) {
    case 'employer':
      return 'Employer';
    case 'consultant':
      return 'Consultant';
    case 'clora_rep':
      return 'Clora Rep';
    default:
      return role;
  }
};
function RoleBubble(_ref) {
  var role = _ref.role,
    children = _ref.children;
  var cls = "msg-bubble ".concat(role);
  return /*#__PURE__*/React.createElement("div", {
    className: cls
  }, /*#__PURE__*/React.createElement("div", {
    className: "bubble-inner"
  }, children));
}
RoleBubble.propTypes = {
  role: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
function ProposalMessages(_ref2) {
  var indexUrl = _ref2.indexUrl,
    createUrl = _ref2.createUrl,
    forwardUrl = _ref2.forwardUrl,
    csrfToken = _ref2.csrfToken,
    isRep = _ref2.isRep,
    isEmployer = _ref2.isEmployer,
    isConsultant = _ref2.isConsultant,
    onAskQuestion = _ref2.onAskQuestion;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    messages = _useState2[0],
    setMessages = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    body = _useState4[0],
    setBody = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    sending = _useState8[0],
    setSending = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    forwarding = _useState10[0],
    setForwarding = _useState10[1];
  var _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    flash = _useState12[0],
    setFlash = _useState12[1];
  var _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    canCreate = _useState14[0],
    setCanCreate = _useState14[1];
  var nameWithLastInitial = function nameWithLastInitial(fullName) {
    var parts = (fullName || '').trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    var first = parts[0];
    var last = parts[parts.length - 1];
    return "".concat(first, " ").concat(last.charAt(0).toUpperCase(), ".");
  };
  var displayName = function displayName(msg) {
    if (isEmployer && msg.sender_role === 'consultant') {
      return nameWithLastInitial(msg.sender_name);
    }
    if (isConsultant && msg.sender_role === 'employer') {
      return 'Employer';
    }
    return msg.sender_name;
  };
  var fetchMessages = function fetchMessages() {
    setLoading(true);
    getRequest(indexUrl).then(function (_ref3) {
      var data = _ref3.data;
      if (Array.isArray(data)) {
        setMessages(data);
      } else if (data && _typeof(data) === 'object') {
        setMessages(data.messages || []);
        if (typeof data.can_create !== 'undefined') setCanCreate(!!data.can_create);
      }
    })["finally"](function () {
      return setLoading(false);
    });
  };
  useEffect(function () {
    fetchMessages();
    var handler = function handler() {
      return fetchMessages();
    };
    window.addEventListener('proposal:question_sent', handler);

    // Clear any nav notifications for this proposal's messages when this view mounts
    try {
      var url = "".concat(window.location.pathname).concat(window.location.search);
      window.dispatchEvent(new CustomEvent('notifications:clear_for_url', {
        detail: {
          url: url
        }
      }));
    } catch (e) {/* noop */}

    // Subscribe to Action Cable for realtime updates
    var subscription;
    try {
      if (window.proposalResourceId) {
        var consumer = window.App && window.App.cable || window.ActionCable && window.ActionCable.createConsumer && window.ActionCable.createConsumer();
        if (consumer) {
          subscription = consumer.subscriptions.create({
            channel: 'ProposalMessagesChannel',
            resource_id: window.proposalResourceId
          }, {
            received: function received(data) {
              try {
                if (!data || _typeof(data) !== 'object') return;
                if (data.type === 'status_update') {
                  // Lightweight: just refetch for now
                  fetchMessages();
                  return;
                }
                if (isConsultant && data.message && data.message.sender_role === 'employer' && !data.message.forwarded_to_consultant) return;
                if (isEmployer && data.message && data.message.sender_role === 'consultant' && !data.message.forwarded_to_employer) return;
                fetchMessages();
              } catch (err) {
                console.error('Error processing realtime message:', err);
              }
            }
          });
        }
      }
    } catch (e) {/* noop */}
    return function () {
      window.removeEventListener('proposal:question_sent', handler);
      try {
        if (subscription && subscription.consumer) {
          subscription.consumer.subscriptions.remove(subscription);
        }
      } catch (e) {/* noop */}
    };
  }, []);
  var canSend = canCreate !== null ? canCreate : isRep || isConsultant || isEmployer;
  var handleSend = function handleSend() {
    if (!body.trim()) return;
    setSending(true);
    postRequest(createUrl, {
      body: body
    }, csrfToken).then(function () {
      setBody('');
      fetchMessages();
    })["finally"](function () {
      return setSending(false);
    });
  };
  var handleForward = function handleForward(msg) {
    if (!msg || !msg.forward_url) return;
    // Confirmation dialog
    // eslint-disable-next-line no-alert
    var ok = window.confirm(msg.sender_role === 'employer' ? 'Forward this message to the consultant?' : 'Forward this message to the employer?');
    if (!ok) return;
    setForwarding(true);
    postRequest(msg.forward_url, {}, csrfToken).then(function (_ref4) {
      var data = _ref4.data;
      setFlash(data.message || 'Question forwarded');
      fetchMessages();
      setTimeout(function () {
        return setFlash('');
      }, 4000);
    })["catch"](function (e) {
      setFlash((e === null || e === void 0 ? void 0 : e.message) || 'Unable to forward the message');
      setTimeout(function () {
        return setFlash('');
      }, 4000);
    })["finally"](function () {
      return setForwarding(false);
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-messages"
  }, flash && /*#__PURE__*/React.createElement("div", {
    className: "flash-success"
  }, flash), loading ? /*#__PURE__*/React.createElement("div", {
    className: "loading"
  }, "Loading messages...") : /*#__PURE__*/React.createElement(Fragment, null, messages.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "thread"
  }, messages.map(function (m) {
    return /*#__PURE__*/React.createElement("div", {
      key: m.id,
      className: "msg-row ".concat(m.sender_role)
    }, /*#__PURE__*/React.createElement(RoleBubble, {
      role: m.sender_role
    }, /*#__PURE__*/React.createElement("div", {
      className: "body"
    }, m.body), /*#__PURE__*/React.createElement("div", {
      className: "meta"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sender"
    }, displayName(m), ' ', /*#__PURE__*/React.createElement("span", {
      className: "role-inline"
    }, "(", roleLabel(m.sender_role), ")")), /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }, " \u2022 "), /*#__PURE__*/React.createElement("span", {
      className: "time"
    }, new Date(m.created_at).toLocaleString()), /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }, " \u2022 "), /*#__PURE__*/React.createElement("span", {
      className: "badge status-badge status-".concat(m.status || (m.forwarded ? 'delivered' : 'sent'))
    }, (m.status || (m.forwarded ? 'delivered' : 'sent')).charAt(0).toUpperCase() + (m.status || (m.forwarded ? 'delivered' : 'sent')).slice(1)), isRep && (m.sender_role === 'employer' || m.sender_role === 'consultant') && /*#__PURE__*/React.createElement(Fragment, null, isRep && !m.forwarded && m.forward_url && /*#__PURE__*/React.createElement("button", {
      className: "btn btn-primary ms-2 ".concat(forwarding ? 'disabled' : ''),
      onClick: function onClick() {
        return handleForward(m);
      }
    }, "Forward")))));
  })), /*#__PURE__*/React.createElement("div", {
    className: "composer"
  }, isEmployer && /*#__PURE__*/React.createElement("div", {
    className: "hint"
  }, "You can send a message here or use \"Ask a Question\" to contact Clora directly.", onAskQuestion && /*#__PURE__*/React.createElement("button", {
    className: "btn btn--link-blue ms-2",
    onClick: onAskQuestion
  }, "Ask a Question")), canSend && /*#__PURE__*/React.createElement("div", {
    className: "composer-row"
  }, /*#__PURE__*/React.createElement("textarea", {
    value: body,
    onChange: function onChange(e) {
      return setBody(e.target.value);
    },
    maxLength: 10000,
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    placeholder: "Type a message",
    rows: 2
  }), /*#__PURE__*/React.createElement("div", {
    className: "char-count small text-muted",
    style: {
      alignSelf: 'flex-end'
    }
  }, "".concat(body.length, " / 10,000")), /*#__PURE__*/React.createElement("button", {
    className: "btn btn-primary ".concat(sending ? 'disabled' : ''),
    onClick: handleSend
  }, "Send", sending ? '...' : '')))));
}
ProposalMessages.propTypes = {
  indexUrl: PropTypes.string.isRequired,
  createUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  isRep: PropTypes.bool,
  isEmployer: PropTypes.bool,
  isConsultant: PropTypes.bool,
  onAskQuestion: PropTypes.func
};
ProposalMessages.defaultProps = {
  isRep: false,
  isEmployer: false,
  isConsultant: false,
  onAskQuestion: null
};
export default ProposalMessages;